import { useEffect, useState } from "react";
import {
  useInfiniteQuery,
  useQuery,
  type InfiniteData,
} from "@tanstack/react-query";
import { Match } from "effect";
import { ApiError } from "#api.ts";
import { useAuth } from "#auth.tsx";
import { runEffectQuery } from "#effect-query.ts";
import { useOrders } from "#orders-context.tsx";
import {
  activeStates,
  formatMoney,
  formatTime,
  mergeOrders,
  matchesPaymentDevice,
  Order,
  ordersPageSchema,
  unassignedPaymentDevice,
} from "#orders.ts";
import { OrderDialog, OrderLines } from "#order-dialog.tsx";
import { OrderToolbar } from "#order-toolbar.tsx";
import { OrderKitchenNotes } from "#kitchen-notes.tsx";
import { paymentDevicesQueryOptions } from "#payment-devices.tsx";
import {
  locationsQueryOptions,
  matchesLocation,
  unassignedLocation,
} from "#locations.ts";
import { useLanguage } from "#i18n.tsx";
import {
  orderStatusFilters,
  readOrderView,
  writeOrderView,
  type OrderView,
} from "#order-view-search.ts";
import { useViewSearch } from "#use-view-search.ts";
import { OrderHistoryTable } from "#order-history-table.tsx";

type Filter = OrderView["status"];
type HistoryPage = typeof ordersPageSchema.Type;
type HistoryQueryKey = readonly ["orders", "history", Filter];

/** Kitchen queue, scheduled orders, and paginated history with shared realtime updates. */
export function LiveOrdersPage({
  layout = "cards",
}: {
  readonly layout?: "cards" | "table";
}) {
  const { api, state } = useAuth();
  const { language, orderStatus, t } = useLanguage();
  const live = useOrders();
  const [searchParams, applySearch] = useViewSearch();
  const role = state.kind === "authenticated" ? state.role : "kitchen-staff";
  const view = readOrderView(searchParams, role);
  const {
    status: filter,
    deviceIds: deviceFilters,
    locationIds: locationFilters,
    tab,
  } = view;
  const applyView = (next: OrderView) => {
    const nextSearch = writeOrderView(searchParams, next);
    const unchanged =
      nextSearch.toString() === writeOrderView(searchParams, view).toString();
    if (layout === "table" && !unchanged) nextSearch.delete("page");
    applySearch(nextSearch, { replace: unchanged });
  };
  const setTab = (next: OrderView["tab"]) => applyView({ ...view, tab: next });
  const [draftFilter, setDraftFilter] = useState<Filter>("active");
  const locationsQuery = useQuery(
    locationsQueryOptions(
      api,
      state.kind === "authenticated" ? state.userId : undefined,
    ),
  );
  const merchantId =
    locationsQuery.data?.merchantId ?? live.orders[0]?.merchantId;
  const [draftLocationFilters, setDraftLocationFilters] = useState<
    readonly string[]
  >([]);
  const [draftDeviceFilters, setDraftDeviceFilters] = useState<
    readonly string[]
  >([]);
  const [selectedId, setSelectedId] = useState<string>();
  const [now, setNow] = useState(Date.now);
  const availableFilters = orderStatusFilters(role);

  const usesHistory =
    filter === "all" || (filter !== "active" && !activeStates.includes(filter));
  const historyQuery = useInfiniteQuery<
    HistoryPage,
    ApiError,
    InfiniteData<HistoryPage, string | undefined>,
    HistoryQueryKey,
    string | undefined
  >({
    queryKey: ["orders", "history", filter],
    enabled: usesHistory,
    retry: false,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => {
      const query = new URLSearchParams({ limit: "100", order: "desc" });

      if (filter !== "all" && filter !== "active") query.set("state", filter);

      if (pageParam) query.set("cursor", pageParam);

      return runEffectQuery(
        api.requestEffect({
          path: `/orders?${query}`,
          schema: ordersPageSchema,
          signal,
        }),
        signal,
      );
    },
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15_000);

    return () => clearInterval(timer);
  }, []);
  const history =
    historyQuery.data?.pages.reduce<readonly Order[]>(
      (current, page) => mergeOrders(current, page.orders),
      [],
    ) ?? [];
  const source = usesHistory ? mergeOrders(history, live.orders) : live.orders;
  const locationNames = new Map(
    (locationsQuery.data?.locations ?? []).map((location) => [
      location.id,
      location.name,
    ]),
  );
  for (const order of source) {
    if (order.locationId && !locationNames.has(order.locationId))
      locationNames.set(order.locationId, order.locationId);
  }
  for (const id of locationFilters) {
    if (id !== unassignedLocation && !locationNames.has(id))
      locationNames.set(id, id);
  }
  const locations = [...locationNames].sort((a, b) => a[1].localeCompare(b[1]));
  const locationLabel = locationFilters
    .map((id) =>
      id === unassignedLocation
        ? t("orders.unassignedLocation")
        : (locationNames.get(id) ?? id),
    )
    .join(" + ");

  // The active-order requests already paginate every state to completion, so
  // device filtering can stay local without another DynamoDB index.
  const paymentDevices =
    useQuery(paymentDevicesQueryOptions(api, merchantId)).data?.devices ?? [];
  const deviceNames = new Map(
    paymentDevices.map((device) => [device.device_id, device.name]),
  );
  for (const id of deviceFilters) {
    if (id !== unassignedPaymentDevice && !deviceNames.has(id))
      deviceNames.set(id, id);
  }

  const matching = source.filter(
    (order) =>
      (filter === "all" ||
        (filter === "active" ? order.active : order.state === filter)) &&
      matchesPaymentDevice(order, deviceFilters) &&
      matchesLocation(order, locationFilters),
  );

  const scheduled = matching
    .filter((order) => order.isScheduled(now))
    .sort((a, b) => (a.scheduledAt ?? "").localeCompare(b.scheduledAt ?? ""));

  const current = matching
    .filter((order) => !order.isScheduled(now))
    .sort((a, b) =>
      usesHistory
        ? b.createdAt.localeCompare(a.createdAt)
        : a.createdAt.localeCompare(b.createdAt),
    );

  const visible =
    layout === "table"
      ? [...matching].sort((a, b) =>
          usesHistory
            ? b.createdAt.localeCompare(a.createdAt)
            : a.createdAt.localeCompare(b.createdAt),
        )
      : tab === "scheduled"
        ? scheduled
        : current;

  const selected = source.find((order) => order.id === selectedId);
  const ordersQuery = usesHistory ? historyQuery : live.query;
  const filterLabel =
    filter === "active"
      ? t("orders.active")
      : filter === "all"
        ? t("orders.all")
        : orderStatus(filter);
  const selectedDeviceLabels = deviceFilters.map((deviceId) =>
    deviceId === unassignedPaymentDevice
      ? t("orders.unassignedDevice")
      : (deviceNames.get(deviceId) ?? deviceId),
  );
  const deviceLabel =
    selectedDeviceLabels.length === 0
      ? undefined
      : selectedDeviceLabels.length <= 2
        ? selectedDeviceLabels.join(" + ")
        : t("orders.deviceCount", { count: selectedDeviceLabels.length });
  const toggleDraftDevice = (deviceId: string) =>
    setDraftDeviceFilters((current) =>
      current.includes(deviceId)
        ? current.filter((selected) => selected !== deviceId)
        : [...current, deviceId],
    );

  return (
    <div>
      <OrderToolbar
        title={layout === "table" ? t("orders.historyTitle") : t("nav.orders")}
        summary={[filterLabel, locationLabel, deviceLabel]
          .filter(Boolean)
          .join(" · ")}
        modified={
          filter !== "active" ||
          deviceFilters.length > 0 ||
          locationFilters.length > 0
        }
        open={() => {
          if (locationsQuery.isStale && !locationsQuery.isFetching)
            void locationsQuery.refetch();
          setDraftFilter(filter);
          setDraftDeviceFilters(deviceFilters);
          setDraftLocationFilters(locationFilters);
        }}
        apply={() => {
          applyView({
            ...view,
            status: draftFilter,
            deviceIds: draftDeviceFilters,
            locationIds: draftLocationFilters,
          });
        }}
        reset={() => {
          setDraftFilter("active");
          setDraftDeviceFilters([]);
          setDraftLocationFilters([]);
        }}
        resetApplied={() => {
          applyView({
            ...view,
            status: "active",
            deviceIds: [],
            locationIds: [],
          });
          setDraftFilter("active");
          setDraftDeviceFilters([]);
          setDraftLocationFilters([]);
        }}
        heading={
          layout === "cards" ? (
            <>
              <h1 className="sr-only">{t("orders.kitchenTitle")}</h1>
              <div
                className="order-tabs"
                role="tablist"
                aria-label={t("orders.kitchenQueues")}
              >
                {(["live", "scheduled"] as const).map((view) => (
                  <button
                    key={view}
                    type="button"
                    role="tab"
                    id={`queue-tab-${view}`}
                    aria-controls="queue-panel"
                    aria-selected={tab === view}
                    tabIndex={tab === view ? 0 : -1}
                    onClick={() => setTab(view)}
                    onKeyDown={(event) => {
                      if (
                        !["ArrowLeft", "ArrowRight", "Home", "End"].includes(
                          event.key,
                        )
                      )
                        return;
                      event.preventDefault();

                      const next =
                        event.key === "Home"
                          ? "live"
                          : event.key === "End"
                            ? "scheduled"
                            : view === "live"
                              ? "scheduled"
                              : "live";

                      setTab(next);
                      document.getElementById(`queue-tab-${next}`)?.focus();
                    }}
                  >
                    {view === "live" ? t("nav.orders") : t("orders.scheduled")}
                    <span
                      className="count"
                      aria-label={t("orders.count", {
                        count:
                          view === "live" ? current.length : scheduled.length,
                      })}
                    >
                      {view === "live" ? current.length : scheduled.length}
                    </span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <h1>
              {t("orders.historyTitle")}{" "}
              <span
                className="count"
                aria-label={t("orders.count", { count: visible.length })}
              >
                {visible.length}
              </span>
            </h1>
          )
        }
      >
        <h3>{t("orders.orderStatus")}</h3>
        <div
          className="filters"
          role="group"
          aria-label={t("orders.orderStatus")}
        >
          {availableFilters.map((item) => (
            <button
              key={item}
              type="button"
              aria-pressed={draftFilter === item}
              className={draftFilter === item ? "active" : ""}
              onClick={() => setDraftFilter(item)}
            >
              {item === "active"
                ? t("orders.active")
                : item === "all"
                  ? t("orders.all")
                  : orderStatus(item)}
            </button>
          ))}
        </div>
        <h3>{t("orders.locations")}</h3>
        {locationsQuery.isPending && (
          <p role="status">{t("orders.locationsLoading")}</p>
        )}
        <div
          className="filters"
          role="group"
          aria-label={t("orders.locations")}
        >
          <button
            type="button"
            aria-pressed={draftLocationFilters.length === 0}
            className={draftLocationFilters.length === 0 ? "active" : ""}
            onClick={() => setDraftLocationFilters([])}
          >
            {t("orders.allLocations")}
          </button>
          {[
            ...locations,
            [unassignedLocation, t("orders.unassignedLocation")],
          ].map(([id, name]) => (
            <button
              key={id}
              type="button"
              aria-pressed={draftLocationFilters.includes(id)}
              className={draftLocationFilters.includes(id) ? "active" : ""}
              onClick={() =>
                setDraftLocationFilters((selected) =>
                  selected.includes(id)
                    ? selected.filter((value) => value !== id)
                    : [...selected, id],
                )
              }
            >
              {name}
            </button>
          ))}
        </div>
        {locationsQuery.isError && (
          <p role="status" className="muted">
            {t("orders.locationsUnavailable")}
          </p>
        )}
        {usesHistory && (
          <p className="muted">{t("orders.locationHistoryHelp")}</p>
        )}
        <h3>{t("orders.paymentDevice")}</h3>
        <div
          className="filters"
          role="group"
          aria-label={t("orders.paymentDevice")}
        >
          <button
            type="button"
            aria-pressed={draftDeviceFilters.length === 0}
            className={draftDeviceFilters.length === 0 ? "active" : ""}
            onClick={() => setDraftDeviceFilters([])}
          >
            {t("orders.allDevices")}
          </button>
          {[...deviceNames].map(([id, name]) => (
            <button
              key={id}
              type="button"
              aria-pressed={draftDeviceFilters.includes(id)}
              className={draftDeviceFilters.includes(id) ? "active" : ""}
              onClick={() => toggleDraftDevice(id)}
            >
              {name}
            </button>
          ))}
          <button
            type="button"
            aria-pressed={draftDeviceFilters.includes(unassignedPaymentDevice)}
            className={
              draftDeviceFilters.includes(unassignedPaymentDevice)
                ? "active"
                : ""
            }
            onClick={() => toggleDraftDevice(unassignedPaymentDevice)}
          >
            {t("orders.unassignedDevice")}
          </button>
        </div>
        <p className="muted">
          {t("orders.activeHelp")}
          {state.kind === "authenticated" && state.role === "admin"
            ? ` ${t("orders.historyHelp")}`
            : ""}
        </p>
        <p className="muted">
          {live.connection === "live"
            ? t("orders.live")
            : t("orders.reconnecting")}
        </p>
      </OrderToolbar>
      {live.notice && (
        <p className="alert" role="status">
          {live.notice}
        </p>
      )}
      {Match.value(ordersQuery).pipe(
        Match.when({ status: "pending" }, () => (
          <p role="status">{t("orders.loading")}</p>
        )),
        Match.when({ status: "error" }, ({ error }) =>
          Match.valueTags(error, {
            ApiError: (failure) => (
              <p className="alert" role="alert">
                {failure.message}
              </p>
            ),
          }),
        ),
        Match.when({ status: "success" }, () => null),
        Match.exhaustive,
      )}
      {/* Retain loaded rows and bulk results if a background refresh fails. */}
      {ordersQuery.data !== undefined && (
        <>
          <div
            id="queue-panel"
            role={layout === "cards" ? "tabpanel" : undefined}
            aria-labelledby={
              layout === "cards" ? `queue-tab-${tab}` : undefined
            }
            aria-busy={ordersQuery.isFetching}
          >
            {layout === "table" ? (
              <OrderHistoryTable
                orders={visible}
                status={filter}
                statuses={availableFilters}
                changeStatus={(status) => applyView({ ...view, status })}
                openOrder={setSelectedId}
                hasOlder={usesHistory && Boolean(historyQuery.hasNextPage)}
              />
            ) : visible.length === 0 ? (
              <div className="empty">
                <img
                  className="brand-logo large"
                  src="/title-icon.png"
                  alt=""
                />
                <h2>
                  {layout === "cards" && tab === "scheduled"
                    ? t("orders.noScheduled")
                    : filter === "active"
                      ? t("orders.noPending")
                      : t("orders.noView")}
                </h2>
                <p>
                  {layout === "cards" && tab === "scheduled"
                    ? "Future orders move to the Orders tab 15 minutes before pickup."
                    : filter === "active"
                      ? "Take a breather. New orders will appear here."
                      : "Try another status or load older orders."}
                </p>
              </div>
            ) : (
              <div className="card-grid">
                {visible.map((order) => (
                  <article
                    className={`order-card ${order.state}`}
                    key={order.id}
                  >
                    <button
                      className="order-card-button"
                      onClick={() => setSelectedId(order.id)}
                      aria-label={`Open order ${order.label}`}
                    >
                      <div className="card-row">
                        <span className={`pill ${order.state}`}>
                          {orderStatus(order.state)}
                        </span>
                        <strong className="pickup-label">{order.label}</strong>
                      </div>
                      {order.state === "order_ready" && (
                        <p className="order-state-hint">
                          {t("orders.awaitingPickup")}
                        </p>
                      )}
                      {order.state === "order_paid_and_in_progress" && (
                        <p className="order-state-hint">
                          {t("orders.preparing")}
                        </p>
                      )}
                      <OrderKitchenNotes order={order} />
                      <OrderLines order={order} visibility="staff" />
                      <div className="card-footer">
                        <time dateTime={order.createdAt}>
                          {formatTime(order.createdAt, false, language)}
                        </time>
                        <strong>{formatMoney(order.total)}</strong>
                      </div>
                      {order.scheduledAt && (
                        <p className="schedule-label">
                          {t("dialog.scheduledFor", {
                            time: formatTime(order.scheduledAt, true, language),
                          })}
                        </p>
                      )}
                    </button>
                  </article>
                ))}
              </div>
            )}
          </div>
          {usesHistory && historyQuery.hasNextPage && (
            <button
              className="secondary load-more"
              disabled={historyQuery.isFetchingNextPage}
              onClick={() => void historyQuery.fetchNextPage()}
            >
              {historyQuery.isFetchingNextPage
                ? t("orders.loadingOlder")
                : t("orders.loadOlder")}
            </button>
          )}
        </>
      )}
      {selected && (
        <OrderDialog
          key={selected.id}
          order={selected}
          close={() => setSelectedId(undefined)}
        />
      )}
    </div>
  );
}
